import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { featuredMovies, latestMovies, popularMovies, categories, movies } from '@/mocks/movies';

export default function Home() {
  const heroMovie = featuredMovies[0];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '97影院',
    url: 'https://www.97cinema.com',
    description: '97影院提供免费高清影院在线观看，高清电影、高清电视剧全集免费看，超清蓝光画质，不缓冲流畅播放，极致视觉享受。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.97cinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '97影院',
    url: 'https://www.97cinema.com',
    logo: 'https://www.97cinema.com/logo.png',
    description: '免费高清影院在线观看平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9797',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src={heroMovie.bannerImage}
              alt={heroMovie.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-12">
                    <div className="hidden lg:block w-60 flex-shrink-0 rounded-lg overflow-hidden shadow-2xl">
                      <img
                        src={heroMovie.posterImage}
                        alt={heroMovie.title}
                        className="w-full aspect-[2/3] object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 pt-16 md:pt-20 lg:pt-0">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 text-xs rounded-full bg-primary-500 text-white font-medium whitespace-nowrap">
                          正在热映
                        </span>
                        <span className="text-white/80 text-sm">{heroMovie.year}</span>
                        <span className="text-white/80 text-sm">{heroMovie.duration}</span>
                      </div>
                      <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
                        {heroMovie.title}
                      </h1>
                      <p className="text-white/80 text-sm md:text-base max-w-xl line-clamp-2 mb-4">
                        {heroMovie.englishTitle}
                      </p>
                      <p className="text-white/60 text-sm max-w-lg line-clamp-2 mb-6">
                        {heroMovie.description}
                      </p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <Link
                          to={`/article/${heroMovie.id}`}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-play-circle-fill text-lg"></i>
                          立即观看
                        </Link>
                        <Link
                          to="/category/all"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/15 text-white font-medium text-sm hover:bg-white/25 transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-film-line"></i>
                          浏览全部
                        </Link>
                      </div>
                      <div className="flex items-center gap-4 mt-6">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          <span className="text-white font-semibold">{heroMovie.rating}</span>
                          <span className="text-white/60 text-xs">/ 10</span>
                        </div>
                        <span className="text-white/40">|</span>
                        <span className="text-white/60 text-sm">导演: {heroMovie.director}</span>
                        <span className="text-white/40">|</span>
                        <span className="text-white/60 text-sm">主演: {heroMovie.cast.slice(0, 3).join('、')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">热门分类</h2>
                <div className="h-4 w-px bg-background-300"></div>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {categories.filter(c => c.slug !== 'all').map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">推荐影片</h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                  更多 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {featuredMovies.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">最新上线</h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                  更多 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {latestMovies.slice(0, 6).map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">热门排行</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularMovies.slice(0, 6).map((movie, index) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group flex items-center gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-200/50 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold ${
                        index < 3 ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-500'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="w-14 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={movie.posterImage} alt={movie.title} className="w-full h-full object-cover object-top" title={`${movie.title} - 97影院高清在线观看`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{movie.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{movie.category} · {movie.year}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-xs text-foreground-600">{movie.rating}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">全部影片</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {movies.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-950">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">畅享高清影视，尽在97影院</h2>
              <p className="text-foreground-400 text-sm md:text-base mb-6">
                海量高清电影、热门电视剧全集免费观看，超清蓝光画质搭配流畅播放体验，让每一帧都清晰动人。
                无需注册，即刻开启您的视觉盛宴。
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                开始观影
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}